import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
function ServicesTable({ services, onEdit, onDelete }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ getValue }) => (
          <Typography variant="body2" fontWeight={600}>
            {getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ getValue }) => (
          <Typography variant="body2" color="text.secondary">
            {getValue() || '-'}
          </Typography>
        ),
      },
      {
        accessorKey: 'duration',
        header: 'Duration',
        cell: ({ getValue }) => (
          <Typography variant="body2">{getValue()} min</Typography>
        ),
      },
      {
        accessorKey: 'price',
        header: 'Price',
        cell: ({ getValue }) => (
          <Typography variant="body2" fontWeight={500}>
            £{getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ getValue }) => (
          getValue() ? (
            <Chip
              label={getValue()}
              variant="outlined"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )
        ),
      },
      {
        accessorKey: 'active',
        header: 'Status',
        cell: ({ getValue }) => (
          <Chip
            label={getValue() ? 'Active' : 'Inactive'}
            color={getValue() ? 'success' : 'error'}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={<EditIcon />}
              onClick={() => onEdit(row.original)}
              sx={{ minWidth: 80 }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(row.original)}
              sx={{ minWidth: 90 }}
            >
              Delete
            </Button>
          </Box>
        ),
      },
    ],
    [onEdit, onDelete]
  );

  const table = useReactTable({
    data: services,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (services.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'grey.50',
          borderRadius: 2,
          color: 'text.secondary',
        }}
      >
        <Typography variant="body1">No services yet. Create your first service above.</Typography>
      </Box>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {services.map((service) => (
          <Paper
            key={service.id}
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="h6" fontWeight={600}>
                {service.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {service.category && (
                  <Chip
                    label={service.category}
                    variant="outlined"
                    size="small"
                  />
                )}
                <Chip
                  label={service.active ? 'Active' : 'Inactive'}
                  color={service.active ? 'success' : 'error'}
                  size="small"
                />
              </Box>
            </Box>

            {service.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {service.description}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Duration
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {service.duration} min
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Price
                </Typography>
                <Typography variant="body1" fontWeight={600} color="primary">
                  £{service.price}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Button
                variant="outlined"
                color="warning"
                fullWidth
                startIcon={<EditIcon />}
                onClick={() => onEdit(service)}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                startIcon={<DeleteIcon />}
                onClick={() => onDelete(service)}
              >
                Delete
              </Button>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // Desktop table view
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell key={header.id} sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} hover>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ServicesTable;
